import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Users, Briefcase } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { AUTH_LINKS } from "@/data/auth-links";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/brands", label: "Brands" },
  { to: "/creators", label: "Creators" },
  { to: "/ugc", label: "UGC" },
  { to: "/agency", label: "Agency" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#050505]/85 border-b border-[#1a1a1a]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <Logo className="h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-[#FF4A00]"
                    : "text-[#A3A3A3] hover:text-[#F3E8E0]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-signup-trigger"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#FF4A00] text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-[#E64300] transition-all active:scale-95"
            >
              Sign up <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#0c0c0c] border-[#222] text-[#F3E8E0] w-64"
              data-testid="nav-signup-menu"
            >
              <DropdownMenuLabel className="text-[#A3A3A3] text-xs uppercase tracking-widest">
                Get started as
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#1a1a1a]" />
              <DropdownMenuItem asChild className="focus:bg-[#1a1a1a] focus:text-[#FF4A00] cursor-pointer">
                <Link
                  to={AUTH_LINKS.brandSignUp}
                  data-testid="nav-signup-brand"
                  className="flex items-start gap-3 py-2"
                >
                  <Briefcase className="w-4 h-4 text-[#FF4A00] mt-1" />
                  <span>
                    <span className="block font-medium">Brand / Agency</span>
                    <span className="block text-xs text-[#A3A3A3]">Run influencer campaigns</span>
                  </span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="focus:bg-[#1a1a1a] focus:text-[#FF4A00] cursor-pointer">
                <Link
                  to={AUTH_LINKS.creatorSignUp}
                  data-testid="nav-signup-creator"
                  className="flex items-start gap-3 py-2"
                >
                  <Users className="w-4 h-4 text-[#FF4A00] mt-1" />
                  <span>
                    <span className="block font-medium">Creator</span>
                    <span className="block text-xs text-[#A3A3A3]">Get brand collaborations</span>
                  </span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-[#222] text-[#F3E8E0]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden absolute top-20 left-0 right-0 bg-[#0a0a0a] border-b border-[#1a1a1a]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={closeMenu}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl font-medium transition-colors ${
                      isActive
                        ? "bg-[#FF4A00]/10 text-[#FF4A00]"
                        : "text-[#F3E8E0] hover:bg-[#161616]"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}

              <div className="mt-4 pt-4 border-t border-[#1a1a1a] grid grid-cols-2 gap-2">
                <Link
                  to={AUTH_LINKS.brandSignUp}
                  onClick={closeMenu}
                  data-testid="mobile-signup-brand"
                  className="text-center text-sm font-medium bg-[#FF4A00] text-white px-3 py-3 rounded-xl hover:bg-[#E64300] transition-colors"
                >
                  Brand sign up
                </Link>
                <Link
                  to={AUTH_LINKS.creatorSignUp}
                  onClick={closeMenu}
                  data-testid="mobile-signup-creator"
                  className="text-center text-sm font-medium bg-[#FF4A00] text-white px-3 py-3 rounded-xl hover:bg-[#E64300] transition-colors"
                >
                  Creator sign up
                </Link>
              </div>

              <Link
                to="/contact"
                onClick={closeMenu}
                data-testid="mobile-cta-contact"
                className="mt-3 text-center text-sm text-[#A3A3A3] hover:text-[#FF4A00] py-2"
              >
                Or contact our team →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
